/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2025, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import { PostMessengerService } from '@app/di/channel/window/post-messenger.service';
import { Logger } from '@core/lib/services';
import { BehaviorSubject, Observable } from 'rxjs';
import { ModalsService } from '@app/common/modals/services/modals.service';
import { UtilService } from '@app/util/util.service';
import { MicroAppBase } from './micro-app-base';
import { ModalMicroApp } from './modal-micro-app';
import { WindowMicroApp } from './window-micro-app';

const log = new Logger('MicroAppService');

export type MicroAppType = ModalMicroApp | WindowMicroApp;

@Injectable({
  providedIn: 'root'
})
export class MicroAppService {
  private microApps: MicroAppBase<any>[] = [];
  private _minimizedMicroApps$: BehaviorSubject<MicroAppBase<any>[]>;
  public minimizedMicroApps: Observable<MicroAppBase<any>[]>;

  constructor(private postMessageService: PostMessengerService, private modalService: ModalsService) {
    this._minimizedMicroApps$ = new BehaviorSubject<MicroAppBase<any>[]>([]);
    this.minimizedMicroApps = this._minimizedMicroApps$.asObservable();
  }

  /**
   * Register a micro app for launch and adds it to the registry
   * @param appId Micro app ID/name to launch
   * @param url Micro app URL to launch inf iframe/window
   * @param type Micro app launch type as Modal or Window
   * @param sessionId Microapp unique sessionId for launch
   * @param allowLocalNetworkResource Allow Local Network Resource Access for Iframe in case of Modal Micro App
   * @returns `Promise<WindowMicroApp | ModalMicroApp>`
   */
  async registerMicroApp<MicroAppType>(
    appId: string,
    url: string,
    type: 'Modal' | 'Window',
    sessionId?: string,
    allowLocalNetworkResource = false
  ): Promise<MicroAppType> {
    // Check if already exists
    if (this.findMicroApp(appId)) {
      log.warn(`MicroApp: ${appId} is already registered`);
      return null;
    }
    const sessionID = sessionId || UtilService.getUUID();
    const urlObj = new URL(url);
    let microApp: MicroAppBase<any>;

    if (type === 'Modal') {
      microApp = new ModalMicroApp(
        appId,
        urlObj,
        sessionID,
        allowLocalNetworkResource,
        this.postMessageService,
        this.modalService,
        closedAppId => this.closeMicroApp(closedAppId),
        () => this.updateMinimizedApps()
      );
    } else {
      microApp = new WindowMicroApp(appId, urlObj, sessionID, this.postMessageService, closedAppId =>
        this.closeMicroApp(closedAppId)
      );
    }

    await microApp.launch();
    this.microApps.push(microApp);

    return microApp as unknown as MicroAppType;
  }

  /**
   * Register a modal iframe window for a micro app in the registry
   * @param appWindow Window object of the micro app
   * @param appName The app ID/name of the micro app
   */
  registerAppWindow(appWindow: Window, appName: string): boolean {
    const microApp = this.findMicroApp(appName);
    if (!microApp) {
      log.debug(`MicroApp : No micro app found with name ${appName}`);
      return;
    }

    if (microApp instanceof ModalMicroApp) {
      return microApp.registerWindow(appWindow);
    }
    return false;
  }

  /**
   * Update the minimized apps observable
   */
  private updateMinimizedApps() {
    // Emit a new array with new refrences of objects to trigger Angular change detection
    const minimizedApps = this.microApps
      ?.filter(app => app.minimized)
      .map(app => Object.assign(Object.create(Object.getPrototypeOf(app)), app));
    this._minimizedMicroApps$.next(minimizedApps || []);
  }

  /**
   * Close a specific micro app
   * @param appID The app ID of the micro app to close
   */
  closeMicroApp(appID: string) {
    const index = this.microApps.findIndex(app => app.appID === appID);
    if (index !== -1) {
      this.microApps[index].updateStatus({ appID, closed: true });
      this.microApps.splice(index, 1);
      this.updateMinimizedApps();
    }
  }

  /**
   * Close all micro apps
   */
  closeAllMicroApps() {
    log.debug(`Closing all micro apps (count: ${this.microApps.length})`);
    this.microApps = [];
    this._minimizedMicroApps$.next(this.microApps);
    this.updateMinimizedApps();
  }

  /**
   * Minimize all modal micro apps
   */
  minimizeAllMicroApps() {
    this.microApps.forEach(app => app.microAppType === 'Modal' && !app.minimized && app.minimize());
    this.updateMinimizedApps();
  }

  /**
   * Check if all micro apps are closed
   * @returns True if no micro apps are running, false otherwise
   */
  get allAppsClosed(): boolean {
    return this.microApps.length === 0 || !this.microApps.some(app => app.isActive());
  }

  /**
   * Finds a microapp exists
   * @returns MicroAppBase if no micro exists, null otherwise
   */
  public findMicroApp(appID: string): MicroAppBase<any> | null {
    return this.microApps.find(app => app.appID === appID) || null;
  }
}
